import { print } from 'graphql';
import { gql } from 'graphql-tag';
import type { MigrationStrategies, RxCollection, RxDocument, RxJsonSchema } from 'rxdb';

import { getDateModified } from '../../utilities/utilityFunctions';
import { defaultDoc, getValueFromLocalStorage } from '../share/defaultDoc';

export interface MobileComponentLocationHeirarchy {
  readonly assetId: string;
  readonly id: string;
  readonly name: string;
  readonly type: number;
  readonly trafficSafety: number;
  readonly areaName: string;
}

export interface InspectionList {
  readonly assetId: number;
  readonly assetName: string;
  readonly assetDescription: string;
  readonly inspectionId: string;
  readonly assetInspectionType: string;
  readonly assetInspectionStatusId: number;
  readonly assetInspectionStatus: string;
  readonly site: number;
  readonly discipline: number;
  readonly workPackageId: number;
  readonly inspectorId: number;
  readonly dateCreated: string;
  readonly dateModified: string;
  readonly isLIstampingRequired: boolean;
  readonly inspectionAssignmentComment: string;
  readonly areaList: [MobileComponentLocationHeirarchy];
  readonly areaId: number;
  readonly isAdhoc: boolean;
}

export const inspectionListSchema: RxJsonSchema<InspectionList> = {
  type: 'object',
  version: 1,
  description: 'describes inspection lists',
  primaryKey: 'inspectionId',
  properties: {
    inspectionId: { type: 'string', maxLength: 36 },
    assetId: { type: ['number', 'null'] },
    assetName: { type: ['string', 'null'] },
    assetDescription: { type: ['string', 'null'] },
    assetInspectionType: { type: ['string', 'null'] },
    assetInspectionStatusId: { type: ['number', 'null'] },
    assetInspectionStatus: { type: ['string', 'null'] },
    site: { type: ['number', 'null'] },
    discipline: { type: ['number', 'null'] },
    workPackageId: { type: ['number', 'null'] },
    inspectorId: { type: ['number', 'null'] },
    dateCreated: { type: ['string', 'null'] },
    dateModified: { type: ['string', 'null'] },
    isLIstampingRequired: { type: ['boolean', 'null'] },
    isAdhoc: { type: ['boolean', 'null'] },
    inspectionAssignmentComment: { type: ['string', 'null'] },
    areaList: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          assetId: { type: ['string', 'null'] },
          id: { type: ['string', 'null'] },
          name: { type: ['string', 'null'] },
          type: { type: ['number', 'null'] },
          trafficSafety: { type: ['number', 'null'] },
          areaName: { type: ['string', 'null'] },
        },
      },
    },
    areaId: { type: ['number', 'null'] },
  },
};

export type InspectionListCollection = RxCollection<InspectionList> | null;
export type InspectionListDocument = RxDocument<InspectionList>;

export const inspectionsListMigrationStrategies: MigrationStrategies = {
  1: (oldDoc: InspectionListDocument) => ({ ...oldDoc, isAdhoc: false }),
};

export const inspectionListPullQueryBuilder = (document: InspectionList) => {
  let doc = document;
  if (!doc) {
    (doc as unknown) = { ...defaultDoc };
  }
  const query = print(gql`
    query assetInspectionsFeed(
      $siteId: Int!
      $disciplineId: Int!
      $lastModified: DateTime!
      $isLeadInspector: Boolean!
    ) {
      assetInspectionsFeed(
        siteId: $siteId
        disciplineId: $disciplineId
        lastModified: $lastModified
        isLeadInspector: $isLeadInspector
      ) {
        isAdhoc
        assetId
        assetName
        assetDescription
        inspectionId
        assetInspectionType
        assetInspectionStatusId
        assetInspectionStatus
        site
        discipline
        workPackageId
        inspectorId
        dateCreated
        dateModified
        isLIstampingRequired
        inspectionAssignmentComment
        areaList {
          assetId
          id
          name
          type
          trafficSafety
          areaName
        }
        areaId
      }
    }
  `);
  return {
    query,
    variables: {
      siteId: JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
      disciplineId: JSON.parse(getValueFromLocalStorage('selectedDiscipline'))
        ?.selectedDisciplineId,
      lastModified: getDateModified(
        doc?.dateModified,
        doc?.dateCreated,
        doc.site,
        doc.discipline,
        JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
        JSON.parse(getValueFromLocalStorage('selectedDiscipline'))?.selectedDisciplineId,
      ),
      isLeadInspector: getValueFromLocalStorage('isLeadInspector') === 'True',
    },
  };
};

export default {};
